import * as React from 'react';
import { styled } from '@glitz/react';
import { translate, useMatchLargeViewport } from '@avensia/excite';
import Link from 'webapp/components/Link';
import { SquareImage } from 'webapp/components/image';
import { createMinLargeViewportDecorator, createMaxSmallViewportDecorator } from 'webapp/viewport';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { scrollbarDecorator } from 'webapp/decorators/scrollbar';
import Price from 'nordicnest/product/Price';
import { CheckboxField } from 'webapp/components/form/checkbox-field';
import { applyPercentagePrice } from './helpers';
import { linkTitle } from 'webapp/product/product-seo-info';
import { Variant } from 'webapp/product/price-variant';

type Props = {
  accessories: NordicNest.Api.ProductAccessoryInfoApiModel[] | undefined;
  updateAccessoriesAdded: (
    accessories: (NordicNest.Api.PartialProductApiModel & { quantity: number })[],
    remove?: boolean,
  ) => void;
  quantity: number;
};

export const ProductAccessoryCards = function ProductAccessories({
  accessories,
  updateAccessoriesAdded,
  quantity,
}: Props) {
  const isLargeViewport = useMatchLargeViewport();

  const validAccessories = accessories?.filter(a => a.quantity && a.quantity > 0);
  if (!validAccessories || validAccessories.length === 0) return null;

  return (
    <AccessoriesWrapper>
      {isLargeViewport && <Header>{translate(t => t.productPage.accessoriesPanelHeading)}</Header>}
      <Accessories>
        {validAccessories.map(({ accessoryProduct: product, discountPercent, quantity: accessoryQuantity }) => (
          <Accessory key={product.displayName}>
            <AccessoryContainer>
              <Link title={linkTitle(product)} to={product.url} linkIdentifier={product.ticket}>
                <SquareImage image={product.images[0]} preset={'thumb'} imageCss={paddingDecorator} />
              </Link>
              <AccessoryContent title={linkTitle(product)} to={product.url} linkIdentifier={product.ticket}>
                <BrandAndName css={!product.availableInCountry && paddingRightDecorator}>
                  <Brand>{product.brand}</Brand>
                  <Name>{product.displayName}</Name>
                </BrandAndName>
              </AccessoryContent>
              <PriceAndStockStatus>
                {product.availableInCountry && (
                  <PriceNew
                    prefix={`${quantity * accessoryQuantity} ${translate(t => t.productPage.productOneUnit)} x`}
                    variant={Variant.Compact}
                    price={applyPercentagePrice(product.price, discountPercent)}
                  />
                )}
              </PriceAndStockStatus>
              {product.availableInCountry && (
                <CheckboxWrapper>
                  <Checkbox
                    onChange={e =>
                      e.currentTarget.checked
                        ? updateAccessoriesAdded([{ ...product, quantity: accessoryQuantity }])
                        : updateAccessoriesAdded([{ ...product, quantity: accessoryQuantity }], true)
                    }
                  />
                </CheckboxWrapper>
              )}
            </AccessoryContainer>
          </Accessory>
        ))}
      </Accessories>
    </AccessoriesWrapper>
  );
};

const AccessoriesWrapper = styled.div({
  marginBottom: t => t.spacing.l,
});

const Header = styled.h3({
  fontWeight: 450,
  fontSize: t => t.sharedFontSize.xs,
  marginBottom: t => t.sharedSpacing.xs,
});

const Checkbox = styled(CheckboxField, {});

const Accessories = styled.div(
  scrollbarDecorator,
  createMinLargeViewportDecorator({
    maxHeight: 550,
    overflow: 'auto',
  }),
);

const Accessory = styled.div(
  createMaxSmallViewportDecorator({
    borderTop: {
      width: 1,
      style: 'solid',
      color: t => t.monochrome.color,
    },
  }),
  {
    backgroundColor: 'white',
    ':last-child': {
      margin: {
        bottom: 0,
      },
    },
    marginBottom: t => t.sharedSpacing.m,
  },
);

const AccessoryContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: '72px 1fr 76px 48px',
  columnGap: 8,
  border: { xy: { width: 'thin', color: t => t.borderStrongColor, style: 'solid' } },
});

const AccessoryContent = styled(Link, {
  order: 2,
  flexGrow: 1,
  padding: { y: t => t.spacing.s },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minWidth: 0,
});

const BrandAndName = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const Brand = styled.div(overflowEllipsisDecorator, {
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => t.fontWeight?.bold,
  textTransform: 'uppercase',
  minHeight: '1.5em',
});

const Name = styled.div(overflowEllipsisDecorator, {
  fontSize: t => t.sharedFontSize.xs,
});

const PriceAndStockStatus = styled.div({
  order: 3,
});

const PriceNew = styled(Price, {
  flexDirection: 'column',
  fontSize: t => t.fontSize.xs,
  fontWeight: t => t.fontWeight?.bold,
  justifyContent: 'center',
  alignItems: 'end',
  height: '100%',
});

const CheckboxWrapper = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  order: 4,
});
const paddingDecorator = styled({
  padding: { xy: t => t.spacing.s },
});

const paddingRightDecorator = styled({
  paddingRight: t => t.spacing.s,
});
